import {
    PROPERTY_LICENCE_TYPE_OTHER,
    PROPERTY_LICENCE_TYPE_SPORTRADAR,
} from '@constants/variables';

import {changelogData} from '../changelogData';
import {getNames} from '../utils';
import {BasicData, DropdownMappingFragment} from '../fragments';

const tournament = `
    tournament {
        id
        name
        parent {
            id
            name
        }
        tournament_category {
            id
            name
            sport {
                id
                name
                has_court
            }
        }
    }
`;

const competitorFragment = (field) => (`
    ${field} {
        id
        name
        is_excluded
        country {
            iso_alpha2_code
            name
        }
    }
`);

const avCompetitorFragment = (field) => (`
    ${field} {
        id
        name
        country {
            iso_alpha2_code
            name
        }
    }
`);

const [
    stadium,
    eventType,
    propertyLicense,
    eventStatus,
    rtsCoverage,
    city,
] = getNames([
    'stadium',
    'event_type',
    'property_licence',
    'event_status',
    'rts_coverage',
    'city',
]);

const [
    homeCompetitor,
    awayCompetitor,
    avHomeCompetitor,
    avAwayCompetitor,
] = [
    competitorFragment('home_competitor'),
    competitorFragment('away_competitor'),
    avCompetitorFragment('home_competitor'),
    avCompetitorFragment('away_competitor'),
];

const srEventBody = `
    id
    match {
        id
        has_live_scout
        has_live_odds
        has_underage_competitors
        ${rtsCoverage}
        ${tournament}
        ${stadium}
        ${homeCompetitor}
        ${awayCompetitor}
        start_datetime
    }
    synchronise_times
    ${tournament}
    ${homeCompetitor}
    ${awayCompetitor}
    ${stadium}
`;

export const eventPvHistory = `
    query EventPvHistory($id: ID!) {
        eventPvHistory(id: $id)
    }
`;

export const event = `
    query EventQuery($id: ID!) {
        event(id: $id) {
            id
            description
            duration
            external_description
            start_datetime
            end_datetime
            notes
            has_argos_job_scheduling
            has_spiideo_integration
            event_preset {
                ${BasicData}
            }
            duration
            color
            ${eventType}
            ${eventStatus}
            products {
                id
                short_name
            }
            clip_providers {
                id
                event_content_type {
                    id
                    name
                }
                clip_provider {
                    id
                    name
                }
            }
            property_licence {
                id
                name
                sport {
                    id
                    name
                    has_tv_channel
                }
                licensor {
                    id
                    name
                }
                property_licence_tech_setup {
                    has_argos_job_scheduling
                    has_spiideo_integration
                }
            }
            sr_event {
                id
                match {
                    id
                    has_live_scout
                    has_live_odds
                    has_underage_competitors
                    has_ended
                    ${rtsCoverage}
                    ${tournament}
                    ${homeCompetitor}
                    ${awayCompetitor}
                    match_status {
                        ${BasicData}
                    }
                    league_round_number
                    tournament_round {
                        ${BasicData}
                    }
                    is_cancelled
                    is_postponed
                    is_walkover
                    is_abandoned
                    schedule_state {
                        ${BasicData}
                        description
                        is_shown_as_warning
                    }
                    start_datetime
                    stadium {
                        id
                        name
                        ${city}
                    }
                    started_datetime
                    ended_datetime
                    tv_streaming_encoder {
                        id
                        name
                        stream_url
                    }
                    tv_channels {
                        id
                        name
                    }
                }
                synchronise_times
                ${tournament}
                ${homeCompetitor}
                ${awayCompetitor}
                stadium {
                     id
                     name
                     ${city}
                }
            }
            av_event {
                id
                content_category {
                    id
                    name
                    parent {
                        id
                        name
                        parent {
                            id 
                            name
                        }
                    }
                }
                venue {
                    id
                    name
                    ${city}
                }
                ${avHomeCompetitor}
                ${avAwayCompetitor}
            }
            st_event {
                ${BasicData}
                description
                synchronise_times
                stage {
                  ${BasicData}
                  stage_type {
                      ${BasicData}
                      description
                  }
                  description
                  is_disabled
                  start_datetime
                  end_datetime
                  stadium {
                    ${BasicData}
                  }
                  stage_category {
                    ${BasicData}
                    parent {
                      ${BasicData}
                      sport {
                        ${BasicData}
                      }
                    }
                  }
                }
                stage_category {
                    ${BasicData}
                    sport {
                      ${BasicData}
                    }
                    parent {
                      id
                      name
                      sport {
                        ${BasicData}
                      }
                    }
                    children {
                      id
                      name
                      sport {
                        ${BasicData}
                      }
                    }
                }
                stadium {
                  ${BasicData}
                  city {
                    ${BasicData}
                  }
                }
            }
            has_valid_event_content_for_autobooker_trigger
            ${changelogData}
        }
    }
`;

export const avEventCreateMutation = `
mutation createAvEvent(
    $event: EventInput
    $preset: EventPresetInput
    $home_competitor: ID
    $away_competitor: ID
    $content_category: ID!
    $venue: ID
) {
    createAvEvent(
        event: $event,
        content_category: $content_category,
        home_competitor: $home_competitor,
        away_competitor: $away_competitor,
        venue: $venue,
        preset: $preset
    ) {
        id
        event {
            id
        }
    }
}
`;


export const avEventDeleteMutation = `
mutation deleteEvent($id : ID!) {
    deleteEvent(id : $id)
}
`;

export const srNonExistingEventCreateMutation = `
mutation createSrEvent(
    $event: EventInput
    $preset: EventPresetInput
    $home_competitor: ID
    $away_competitor: ID
    $match: ID
    $tournament: ID
    $stadium: ID
    $synchronise_times: Boolean!
) {
    createSrEvent(
        event: $event,
        match: $match,
        tournament: $tournament,
        stadium: $stadium,
        home_competitor: $home_competitor,
        away_competitor: $away_competitor,
        synchronise_times: $synchronise_times,
        preset: $preset,
    ) {
      id
      event {
        id
      }
    }
}
`;

export const srEventMultipleCreateMutation = `
mutation bulkCreateSrEvents (
    $matches: [ID],
    $property_licence: ID!,
    $description: String,
    $external_description: String,
    $color: String,
    $notes: String,
    $event_status: ID,
    $event_preset: ID,
    $products: [ID],
    $clip_providers: [EventClipProvidersInput]
) {
    bulkCreateSrEvents (
        matches: $matches,
        property_licence: $property_licence,
        description: $description,
        external_description: $external_description,
        color: $color,
        event_status: $event_status,
        event_preset: $event_preset,
        notes: $notes,
        products: $products,
        clip_providers: $clip_providers
    )
}`;

export const srEventCreateMutation = `
mutation createSrEvent(
    $event: EventInput,
    $match: ID,
    $tournament: ID,
    $stadium: ID,
    $synchronise_times: Boolean,
    $preset: EventPresetInput
) {
    createSrEvent(
        event: $event,
        match: $match,
        tournament: $tournament,
        stadium: $stadium,
        synchronise_times: $synchronise_times,
        preset: $preset
    ) {
      id
      event {
        id
      }
    }
}
`;

export const srEventUpdateMutation = `
mutation updateSrEvent(
    $event: EventInput,
    $match: ID,
    $tournament: ID,
    $away_competitor: ID,
    $home_competitor: ID,
    $stadium: ID,
    $synchronise_times: Boolean
    $id: ID!,
    $preset: EventPresetInput
    $as_additional_contents: Boolean
) {
    updateSrEvent(
        event: $event, 
        match: $match, 
        tournament: $tournament, 
        away_competitor: $away_competitor,
        home_competitor: $home_competitor,
        stadium: $stadium,  
        synchronise_times: $synchronise_times
        id: $id,
        preset: $preset
        as_additional_contents: $as_additional_contents
    ) {
        id
        ${srEventBody}
        event {
          id
          description
          external_description
          start_datetime
          end_datetime
          notes
          color
          clip_providers {
              event_content_type {
                  id
                  name
              }
              clip_provider {
                  ${BasicData}
              }
          }
          event_preset {
                id
          }
          products {
              id
              short_name
          }
          ${eventType}
          ${eventStatus}
          ${propertyLicense}
        }
    }
}
`;

export const srEventDeleteMutation = `
mutation deleteEvent($id : ID!) {
    deleteEvent(id : $id)
}
`;
export const optionsQuery = `
    query optionsQuery($matchId: Int) {
         srEvents(filters: {match: $matchId}) {
            event {
                id
            }
        }
        property_licences: propertyLicencesForDropdown (filters: {
            isArchived: false, 
            propertyLicenceType: ${PROPERTY_LICENCE_TYPE_SPORTRADAR},
            match: $matchId
        }) {
            id
            name
            licensor {
                id
                name
            }
            property_licence_tech_setup {
                has_argos_job_scheduling
                has_spiideo_integration
            }
        }
        venues{
            key: id,
            value: id,
            text: name
        }
        event_status :eventStatuses {
            id,
            name,
            key : id,
            value : id,
            text : name
        }
    }
`;

export const nonExistingSportradarOptionsQuery = `
    query optionsQuery {
        property_licences: propertyLicences (filters: {
            isArchived: false, propertyLicenceType: ${PROPERTY_LICENCE_TYPE_SPORTRADAR} 
        })
        event_status :eventStatuses {
            id,
            name,
            key : id,
            value : id,
            text : name
        }
    }
`;

export const avOptionsQuery = `
    query optionsQuery {
        property_licences: propertyLicences (filters: {
            isArchived: false, propertyLicenceType: ${PROPERTY_LICENCE_TYPE_OTHER}
        })
        venues {
            id
            name
            city {
                id
                name
            }
        }
        event_status :eventStatuses {
            id,
            name,
            key : id,
            value : id,
            text : name
        }
    }
`;

export const checkIfMatchIsLinkedToSrEvent = `
    query SrEventsQuery($match: Int) {
        srEvents(filters: {match: $match}) {
            id
        }
    }
`;

export const existingSportradarEventOptions = `
    query options{
        property_licences: propertyLicences (filters: {
                isArchived: false, propertyLicenceType: ${PROPERTY_LICENCE_TYPE_OTHER}
            })
        event_status :eventStatuses {
                id,
                name,
                key : id,
                value : id,
                text : name
            }
    }
`;

export const existingSportradarEventByMatch = `
query existingSportradarEventByMatch($id : ID!){
  match(id: $id) {
    id
    start_datetime
    tournament {
      id
      name
      parent{
        id
        name
      }
      tournament_category{
        id
        name
        sport {
          id
          name
          has_tv_channel
        }
      }
    }
    home_competitor {
      id
      name
    }
    away_competitor {
      id
      name
    }
    stadium {
      id
      name
      city {
        id
        name
      }
    }
    has_live_odds
    has_live_scout
    tv_channels {
      id
      name
    }    
  }
}
`;

export const updateExistingSportradarEvent = `
mutation updateSrEvent(
    $event: EventInput,
    $synchronise_times: Boolean
    $id: ID,
    $preset: EventPresetInput
    $as_additional_contents: Boolean
    $as_additional_argos: Boolean
) {
    updateSrEvent(
        event: $event
        synchronise_times: $synchronise_times
        id: $id
        preset: $preset
        as_additional_contents: $as_additional_contents
        as_additional_argos: $as_additional_argos
    ) {
      id
      synchronise_times
      event {
          id
          description
          external_description
          start_datetime
          end_datetime
          has_argos_job_scheduling
          has_spiideo_integration
          notes
          color
          event_preset {
            id
          }
          ${eventType}
          ${eventStatus}
          ${propertyLicense}
          products {
              id
              short_name
          }
      }
    }
}`;

export const eventScheduleQuery = `
  query EventScheduleQuery (
    $search: String
    $from: DatetimeTzInputType
    $to: DatetimeTzInputType
    $propertyLicence: [ID]
    $eventPreset: [ID]
    $eventType: ID
    $sport: [ID]
    $tvChannels: [ID]
    $category: [ID]
    $tournament: [ID]
    $contentCategoryLevel1: [ID]
    $contentCategoryLevel2: [ID]
    $contentCategoryLevel3: [ID]
    $stadium: [ID]
    $venue: [ID]
    $scheduled: Boolean
    $hasExcludedCompetitor: Boolean
    $hasMatchId: Boolean
    $hasStageId: Boolean
    $eventStatus: [ID]
    $contributionType: [ID]
    $encodingDatacenter: [ID]
    $product: [ID]
    $productStatus: [ID]
    $homeCompetitor: Boolean
    $awayCompetitor: Boolean
    $rtsCoverage: Boolean
    $isTvStreaming: Boolean
    $hasLiveScout: Boolean
    $hasLiveOdds: Boolean
    $hasUnderageCompetitors: Boolean
    $isCancelled: Boolean
    $isPostponed: Boolean
    $isWalkover: Boolean
    $isInterrupted: Boolean
    $isSuspended: Boolean
    $isAbandoned: Boolean
    $isRetired: Boolean
    $report: [ID]
    $eventContentTypes: [ID]
    $eventContentVariants: [ID]
    $coverage: [ID]
    $clipProviders: [ID]
    $matchScheduleStates: [ID]
    $firstStageCategories: [ID]
    $secondStageCategories: [ID]
  ) {
    eventSchedule (filters: {
      search: $search
      from: $from
      to: $to
      propertyLicence: $propertyLicence
      eventPreset: $eventPreset
      eventType: $eventType
      sport: $sport
      tvChannels: $tvChannels
      category: $category
      tournament: $tournament
      contentCategoryLevel1: $contentCategoryLevel1
      contentCategoryLevel2: $contentCategoryLevel2
      contentCategoryLevel3: $contentCategoryLevel3
      stadium: $stadium
      venue: $venue
      scheduled: $scheduled
      hasExcludedCompetitor: $hasExcludedCompetitor
      hasMatchId: $hasMatchId
      hasStageId: $hasStageId
      eventStatus: $eventStatus
      contributionType: $contributionType
      encodingDatacenter: $encodingDatacenter
      product: $product
      productStatus: $productStatus
      homeCompetitor: $homeCompetitor
      awayCompetitor: $awayCompetitor
      rtsCoverage: $rtsCoverage
      isTvStreaming: $isTvStreaming
      hasLiveScout: $hasLiveScout
      hasLiveOdds: $hasLiveOdds
      hasUnderageCompetitors: $hasUnderageCompetitors
      isCancelled: $isCancelled
      isPostponed: $isPostponed
      isWalkover: $isWalkover
      isInterrupted: $isInterrupted
      isSuspended: $isSuspended
      isAbandoned: $isAbandoned
      isRetired: $isRetired
      report: $report
      eventContentType: $eventContentTypes
      eventContentVariant: $eventContentVariants
      coverage: $coverage
      clipProviders: $clipProviders
      scheduleStates: $matchScheduleStates
      firstStageCategories: $firstStageCategories
      secondStageCategories: $secondStageCategories
    }) {
      events
      matches
      stages
    }
  }
`;

export const eventScheduleFiltersQuery = `
  query EventScheduleFiltersQuery {
    clipProviders {
      ${DropdownMappingFragment}
    }
    contributionTypes {
      ${DropdownMappingFragment}
    }
    encodingDatacenters {
      ${DropdownMappingFragment}
    }
    propertyLicences: propertyLicencesForDropdown {
      id
      name
      licensor {
        id
        name
      }
    }
    eventTypes {
      ${DropdownMappingFragment}
    }
    eventContentTypes {
      key: id
      value: id
      text: name
    }
    eventContentVariants {
      ${DropdownMappingFragment}
    }
    eventStatuses {
      ${DropdownMappingFragment}
    }
    productStatuses {
      ${DropdownMappingFragment}
    }
    products {
      key: id
      text: short_name
      value: id
      has_event_booking_support
    }
    venues {
      ${DropdownMappingFragment}
    }
    matchScheduleStates {
      ${DropdownMappingFragment}
    }
  }
`;

export const eventStatuses = `
  query EventStatusesQuery {
    eventStatuses {
      id
      name
      value: id
    }
  }
`;
